import { useMemo } from 'react';
import { generateArticleSchema } from '../utils/schemaGenerators';
import { countrySlugToName } from '../utils/stateUtils';
import type { BreadcrumbItem } from '../types/seo';

interface BillConfig {
  pageTitle: string;
  seoTitle: string;
  seoDescription: string;
}

interface UseBillDetailSEOParams {
  isStateMode: boolean;
  billConfig: BillConfig | undefined | null;
  billSlug: string | undefined;
  stateName: string | null;
  stateSlug: string | null;
  country: string;
  heroImageUrl: string;
}

/**
 * Computes SEO artifacts for the bill detail page:
 * - breadcrumbs: structured breadcrumb trail for schema.org + UI
 * - articleSchema: Article JSON-LD for the page
 */
export function useBillDetailSEO({
  isStateMode,
  billConfig,
  billSlug,
  stateName,
  stateSlug,
  country,
  heroImageUrl,
}: UseBillDetailSEOParams) {
  const canonicalPath = isStateMode && stateSlug ? `/bill/${country}/${stateSlug}/${billSlug}` : `/bill/${billSlug}`;

  const breadcrumbs: BreadcrumbItem[] = useMemo(() => {
    if (!isStateMode || !billConfig) return [];

    const countryName = countrySlugToName(country) || 'United States';
    const items: BreadcrumbItem[] = [
      { name: 'Home', url: '/', position: 1 },
      { name: 'Bills', url: '/family-court-reform-bills', position: 2 },
      { name: countryName, position: 3 },
    ];

    if (stateName) {
      items.push(
        { name: stateName, url: `/bill/${country}/${stateSlug}`, position: 4 },
        { name: billConfig.pageTitle, url: `/bill/${country}/${stateSlug}/${billSlug}`, position: 5 },
      );
    } else {
      items.push({ name: billConfig.pageTitle, url: `/bill/${billSlug}`, position: 4 });
    }

    return items;
  }, [isStateMode, billConfig, billSlug, stateName, stateSlug, country]);

  const articleSchema = useMemo(
    () =>
      billConfig
        ? generateArticleSchema(
            {
              seoTitle: billConfig.seoTitle,
              seoDescription: billConfig.seoDescription,
              imageUrl: heroImageUrl,
              pageUrl: canonicalPath,
            },
            stateName || undefined,
          )
        : undefined,
    [billConfig, canonicalPath, heroImageUrl, stateName],
  );

  return { breadcrumbs, articleSchema, canonicalPath };
}
